import React, { useState, useEffect } from "react";
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";
import { Editor as WysiwygEditor } from 'react-draft-wysiwyg';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "draft-js/dist/Draft.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function EditBlogForm({ blogId, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    title: "",
    category: "",
    body: EditorState.createEmpty(),
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchBlog() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_URL}/api/blogs/${blogId}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
          },
        });
        if (!res.ok) throw new Error("Failed to fetch blog data");
        const data = await res.json();
        setForm({
          title: data.title || "",
          category: data.category || "",
          body: data.body
            ? EditorState.createWithContent(convertFromRaw(typeof data.body === 'string' ? JSON.parse(data.body) : data.body))
            : EditorState.createEmpty(),
        });
        setExistingImages(data.images || []);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    }
    fetchBlog();
    // eslint-disable-next-line
  }, [blogId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEditorChange = (editorState) => {
    setForm({ ...form, body: editorState });
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setThumbnail(file);
    } else {
      setError("Please select a valid image file for thumbnail.");
      setThumbnail(null);
    }
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files).filter((file) => file.type.startsWith("image/"));
    if (files.length > 0) {
      setImages(files);
    } else {
      setError("Please select valid image files.");
      setImages([]);
    }
  };

  const handleRemoveExistingImage = (imgId) => {
    setExistingImages(existingImages.filter(img => img.id !== imgId));
    setRemovedImageIds([...removedImageIds, imgId]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (!localStorage.getItem("access_token")) {
      setError("You must be logged in to edit a blog.");
      setLoading(false);
      return;
    }
    try {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("category", form.category);
      if (thumbnail) formData.append("thumbnail", thumbnail);
      images.forEach((img) => formData.append("image_files", img));
      formData.append("keep_images", JSON.stringify(existingImages.map(img => img.id)));
      if (removedImageIds.length > 0) {
        formData.append("remove_images", JSON.stringify(removedImageIds));
      }
      const contentState = form.body.getCurrentContent();
      const body = JSON.stringify(convertToRaw(contentState));
      if (body.trim() !== "") {
        formData.append("body", body);
      } else {
        setError("Blog body cannot be empty.");
        setLoading(false);
        return;
      }
      const res = await fetch(`${API_URL}/api/blogs/${blogId}/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
        },
        body: formData,
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(JSON.stringify(errorData) || "Failed to update blog");
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 600, margin: "auto", padding: 16 }}>
      <h2>Edit Blog</h2>
      <input
        name="title"
        type="text"
        placeholder="Blog Title"
        value={form.title}
        onChange={handleChange}
        required
        className="input"
      />
      <input
        name="category"
        type="text"
        placeholder="Category"
        value={form.category}
        onChange={handleChange}
        required
        className="input"
      />
      <div style={{ marginTop: 8 }}>
        <label>
          Thumbnail: <input type="file" accept="image/*" onChange={handleThumbnailChange} />
        </label>
      </div>
      <div style={{ marginTop: 8 }}>
        <label>
          Images: <input type="file" accept="image/*" multiple onChange={handleImagesChange} />
        </label>
      </div>
      {existingImages.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <label>Existing Images:</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {existingImages.map(img => (
              <div key={img.id} style={{ position: 'relative' }}>
                <img src={img.url || img.image || img} alt="blog" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                <button type="button" style={{ position: 'absolute', top: 0, right: 0, background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer' }} onClick={() => handleRemoveExistingImage(img.id)}>x</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <label>Body:</label>
        <div style={{ border: "1px solid #ccc", padding: 8, marginTop: 8, height: "300px", overflowY: "auto" }}>
          <WysiwygEditor
            editorState={form.body}
            onEditorStateChange={handleEditorChange}
            toolbar={{
              options: ['inline', 'blockType', 'list', 'textAlign', 'link'],
              inline: { inDropdown: true, options: ['bold', 'italic', 'underline', 'monospace'] },
              blockType: { inDropdown: true, options: ['Normal', 'H1', 'H2', 'H3', 'Blockquote'] },
              list: { inDropdown: true, options: ['unordered', 'ordered'] },
              textAlign: { inDropdown: true, options: ['left', 'center', 'right'] },
              link: { inDropdown: true },
              image: { inDropdown: true }
            }}
          />
        </div>
      </div>
      {error && <div style={{ color: "red", marginTop: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <button
          type="submit"
          className="btn-primary-primary"
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        )}
      </div>
    </form>
  );
}

export default EditBlogForm;
